import { Flex, Image, Typography } from "antd";
import { useParams } from "react-router-dom";
import { Product, ProductParamValue } from "tm-shared/types";

import { FALLBACK_IMAGE } from "@/constants";
import { useCatalog } from "@/domains/product";
import { Header, MainContainer } from "@/shared/layout";
import { Meta } from "@/shared/seo";
import { BlockLoader, PrimaryText, SecondaryText } from "@/shared/ui";
import { getFileUrl } from "@/utils/file";

import {
    CarouselWrapper,
    ImageWrapper,
    StyledCarousel,
    StyledParamRow,
    TaskDescriptionBlock,
} from "./Styled";

const HIDDEN_PARAM_TAGS = new Set(["image", "description", "barcode"]);

const getProductImageList = (
    barcode: string,
    products?: Record<string, Product>,
) => {
    const value = products?.[barcode]?.params?.image?.value;

    if (Array.isArray(value) && value.length > 0) {
        return value;
    }

    return [];
};

const formatParamValue = (value: ProductParamValue) => {
    if (typeof value === "boolean") {
        return value ? "Так" : "Ні";
    }

    return value;
};

function ProductDetails() {
    const { barcode = "" } = useParams();
    const { products, productsLoading } = useCatalog();

    const currentProduct = products[barcode];

    const productName = currentProduct?.name;
    const imagesList = currentProduct
        ? getProductImageList(barcode, products)
        : [];
    const visibleParams = currentProduct?.params
        ? Object.values(currentProduct.params).filter(
              (item) => !HIDDEN_PARAM_TAGS.has(item.paramTag),
          )
        : [];
    const description = currentProduct?.params?.description?.value;

    if (!productsLoading && !currentProduct) {
        return (
            <>
                <Meta title={barcode} />

                <Header back />

                <MainContainer>
                    <Flex flex={1} align="center" justify="center">
                        <Typography.Text>Товар не знайдено</Typography.Text>
                    </Flex>
                </MainContainer>
            </>
        );
    }

    return (
        <>
            <Meta title={productName} />

            <Header title={productName} back />

            <MainContainer>
                {productsLoading ? (
                    <Flex flex={1} align="center" justify="center">
                        <BlockLoader />
                    </Flex>
                ) : (
                    <>
                        <Flex vertical gap={16} style={{ marginBottom: 16 }}>
                            {imagesList?.length > 1 ? (
                                <CarouselWrapper>
                                    <StyledCarousel draggable arrows>
                                        {imagesList?.map(
                                            (item: string, index: number) => (
                                                <Image
                                                    key={index}
                                                    height="300px"
                                                    width="auto"
                                                    src={getFileUrl(item)}
                                                    fallback={FALLBACK_IMAGE}
                                                />
                                            ),
                                        )}
                                    </StyledCarousel>
                                </CarouselWrapper>
                            ) : (
                                <ImageWrapper>
                                    <Image
                                        width="auto"
                                        height="100%"
                                        loading="eager"
                                        src={getFileUrl(imagesList?.[0])}
                                        fallback={FALLBACK_IMAGE}
                                    />
                                </ImageWrapper>
                            )}

                            <SecondaryText textAlign="right">
                                Штрихкод: {barcode}
                            </SecondaryText>
                        </Flex>
                        <Flex vertical gap={16}>
                            <TaskDescriptionBlock>
                                <Flex vertical gap={16}>
                                    {visibleParams.map((item) => (
                                        <StyledParamRow key={item.paramTag}>
                                            <SecondaryText>
                                                {item.paramName}
                                            </SecondaryText>

                                            <SecondaryText>
                                                {formatParamValue(item.value)}
                                            </SecondaryText>
                                        </StyledParamRow>
                                    ))}
                                </Flex>
                            </TaskDescriptionBlock>

                            <TaskDescriptionBlock>
                                <PrimaryText>{description}</PrimaryText>
                            </TaskDescriptionBlock>
                        </Flex>
                    </>
                )}
            </MainContainer>
        </>
    );
}

export default ProductDetails;
